import type { LucideIcon } from "lucide-react";
import {
  Code2,
  Globe,
  Globe2,
  GraduationCap,
  Megaphone,
  Palette,
  Play,
  Search,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
} from "lucide-react";
import { filterByVisibleSlugs } from "./catalog";

/**
 * Die drei Säulen Design · Marketing · Development mit ihren Leistungen –
 * EINE Quelle für das Mega-Menü im Header und „Unsere Dienstleistungen“ auf
 * der Startseite (Auftrag Paket 12, Nr. 1). Namen und Links entsprechen dem
 * Katalog (src/lib/services/catalog.ts); die Sichtbarkeit steuert weiterhin
 * Admin → Dienstleistungen über `filterByVisibleSlugs`.
 *
 * Client-safe: keine Server-Imports (Header und ServicesPreview sind
 * Client-Komponenten).
 */
export type PillarId = "design" | "marketing" | "development";

export type PillarService = {
  slug: string;
  title: string;
  href: string;
  /** Eine Zeile für das Menü. */
  short: string;
  /** Zwei Sätze für die Startseite. */
  description: string;
  icon: LucideIcon;
};

export type Pillar = {
  id: PillarId;
  label: string;
  /** Ein Satz je Säule (Menü und Startseite). */
  tagline: string;
  icon: LucideIcon;
  /** Mockup-Bild der Säule (public/images/mockups) – Startseite. */
  image: { name: string; alt: string };
  services: PillarService[];
};

export const PILLARS: Pillar[] = [
  {
    id: "design",
    label: "Design",
    tagline: "Eine Marke, die man wiedererkennt – vom Logo bis zum Video.",
    icon: Palette,
    image: { name: "branding-brand-board.webp", alt: "Brand-Board der Beispielbetrieb GmbH: Logo-System, Farben, Typografie und Anwendungen" },
    services: [
      {
        slug: "branding",
        title: "Markenidentität",
        href: "/dienstleistungen/branding",
        short: "Logo, Farben, Schrift und Vorlagen – ein System für alles.",
        description: "Logo, Farbsystem, Visitenkarten und Brand Guidelines – ein einheitliches Erscheinungsbild für Ihre Marke.",
        icon: Palette,
      },
      {
        slug: "motion-design",
        title: "Motion Design & Erklärvideos",
        href: "/dienstleistungen/motion-design",
        short: "Erklärvideo in wenigen Wochen, in allen Formaten.",
        description: "Erklärvideo in wenigen Wochen: Skript, Storyboard und Gestaltung von uns, Animation und Sprecherstimme KI-gestützt – in allen Formaten.",
        icon: Play,
      },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    tagline: "Gefunden werden – bei Google, in KI-Antworten und in den sozialen Netzwerken.",
    icon: Megaphone,
    image: { name: "seo-report.webp", alt: "SEO-Monatsbericht der Beispielbetrieb GmbH: Kennzahlen, Rankings nach Ort und Maßnahmen" },
    services: [
      {
        slug: "seo",
        title: "Suchmaschinen-Optimierung",
        href: "/dienstleistungen/seo",
        short: "Sichtbar bei Google und in KI-Antworten – Rankings je Ort.",
        description: "SEO, das Ihre Website bei Google nach vorne bringt. Lokal und überregional, technisch und inhaltlich – mit Monatsbericht.",
        icon: Search,
      },
      {
        slug: "social-media-marketing",
        title: "Social Media Marketing",
        href: "/dienstleistungen/social-media-marketing",
        short: "Reels, Beiträge und Storys – geplant, produziert, ausgewertet.",
        description: "Kurzvideos und Beiträge mit Redaktionsplan – vor Ort produziert, je Plattform geschnitten, monatlich ausgewertet.",
        icon: Share2,
      },
      {
        slug: "paid-ads",
        title: "Online-Werbung",
        href: "/dienstleistungen/paid-ads",
        short: "Google Ads, Meta Ads und LinkedIn Ads – Anfragen ab sofort.",
        description: "Anzeigen bei Google, Meta und LinkedIn – mit passender Landingpage, Messung nach Einwilligung und Monatsbericht.",
        icon: Megaphone,
      },
    ],
  },
  {
    id: "development",
    label: "Development",
    tagline: "Websites und Software, die Arbeit abnehmen – aus Code, nicht aus dem Baukasten.",
    icon: Code2,
    image: { name: "software-kundenportal.webp", alt: "Kundenportal der Beispielbetrieb GmbH mit Projekten, Tickets und Dokumenten" },
    services: [
      {
        slug: "webdesign",
        title: "Website-Entwicklung",
        href: "/dienstleistungen/webdesign",
        short: "Schnelle, barrierefreie Websites mit eigenem Redaktionssystem.",
        description: "Websites aus Code mit Design, eigenem Redaktionssystem, SEO und laufender Pflege – vom ersten Entwurf bis zum fertigen Auftritt.",
        icon: Globe,
      },
      {
        slug: "software-entwicklung",
        title: "Software & App-Entwicklung",
        href: "/dienstleistungen/software-entwicklung",
        short: "Eigene Software und Apps statt monatlicher Lizenzen.",
        description: "Web-Anwendungen, Apps für iPhone und Android und interne Werkzeuge – Code, der Ihnen gehört, statt Lizenzen für fremde Software.",
        icon: Smartphone,
      },
      {
        slug: "ki-analyse",
        title: "KI-Potenzialanalyse",
        href: "/dienstleistungen/ki-analyse",
        short: "Ein Tag vor Ort, danach ein priorisierter Maßnahmenplan.",
        description: "Ein Tag vor Ort, danach ein konkreter Maßnahmenplan: wo KI in Ihrem Betrieb wirklich Zeit spart – und wo nicht.",
        icon: Sparkles,
      },
      {
        slug: "wartungsservice",
        title: "Wartung & Hosting",
        href: "/dienstleistungen/wartungsservice",
        short: "Server in Frankfurt, Updates, Sicherung und Überwachung.",
        description: "Server in Frankfurt, Überwachung rund um die Uhr und laufende Datenschutz-Pflege – als monatlicher Festpreis für Websites von DMD Studio.",
        icon: ShieldCheck,
      },
      {
        slug: "domains",
        title: "Domain-Verwaltung",
        href: "/dienstleistungen/domains",
        short: "Domains auf Ihren Namen – mit DNS, SSL und Ablauf-Überwachung.",
        description: "Domains erwerben, verwalten und pflegen – registriert auf Ihren Namen, nicht unseren. DNS, SSL und Ablauf-Überwachung inklusive.",
        icon: Globe2,
      },
      {
        slug: "ki-schulungen",
        title: "KI-Schulungen",
        href: "/dienstleistungen/ki-schulungen",
        short: "Sicher und datenschutzkonform mit KI arbeiten – offen oder inhouse.",
        description: "Sicher und DSGVO-konform mit KI arbeiten, bessere Ergebnisse erzielen und KI sinnvoll in den Alltag einbinden – offen in Präsenz oder inhouse für Ihr Team.",
        icon: GraduationCap,
      },
    ],
  },
];

/** Säulen mit den öffentlich sichtbaren Leistungen; leere Säulen entfallen. */
export function visiblePillars(visibleSlugs?: string[]): Pillar[] {
  return PILLARS.map((p) => ({ ...p, services: filterByVisibleSlugs(p.services, visibleSlugs) })).filter((p) => p.services.length > 0);
}
